import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  Brain,
  CheckCircle,
  Clock,
  AlertTriangle,
  Info,
  FileSpreadsheet,
  Workflow,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface LogEntry {
  id: string;
  action: string;
  description: string | null;
  status: string;
  created_at: string;
  metadata: any;
}

const statusConfig: Record<string, { icon: React.ElementType; color: string }> = {
  success: { icon: CheckCircle, color: "text-green-600" },
  error: { icon: XCircle, color: "text-destructive" },
  warning: { icon: AlertTriangle, color: "text-yellow-600" },
  info: { icon: Info, color: "text-blue-600" },
};

const actionIcon = (action: string) => {
  if (action.toLowerCase().includes("workflow")) return Workflow;
  if (action.toLowerCase().includes("ai") || action.toLowerCase().includes("model")) return Brain;
  if (action.toLowerCase().includes("import") || action.toLowerCase().includes("data")) return FileSpreadsheet;
  return Info;
};

const Activity = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      const { data, error } = await supabase
        .from("system_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (!error && data) setLogs(data);
      setLoading(false);
    };
    fetchLogs();
  }, []);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Activity</h1>
          <p className="text-muted-foreground">Your recent events and system activity.</p>
        </div>

        <Card className="p-4 md:p-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-4 items-start">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : logs.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No activity yet"
              description="Events from your workflows, AI analyses, and data imports will appear here."
            />
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-6">
                {logs.map((log) => {
                  const cfg = statusConfig[log.status] || statusConfig.info;
                  const ActionIcon = actionIcon(log.action);
                  return (
                    <div key={log.id} className="relative flex gap-4 pl-10">
                      <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                        <ActionIcon className={`h-4 w-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-foreground">{log.action}</span>
                          <Badge
                            variant={log.status === "error" ? "destructive" : "secondary"}
                            className="text-xs"
                          >
                            {log.status}
                          </Badge>
                        </div>
                        {log.description && (
                          <p className="text-sm text-muted-foreground">{log.description}</p>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default Activity;
